import React from 'react';
import { TableCell, TableSortLabel, TableCellProps } from '@mui/material';

export type SortDirection = 'asc' | 'desc';

interface SortableTableCellProps extends Omit<TableCellProps, 'onClick'> {
  label: string;
  sortKey: string;
  orderBy: string;
  order: SortDirection;
  onRequestSort: (sortKey: string) => void;
}

/** Cabecera de tabla clicable con flecha de orden (MUI TableSortLabel). */
const SortableTableCell: React.FC<SortableTableCellProps> = ({
  label,
  sortKey,
  orderBy,
  order,
  onRequestSort,
  ...cellProps
}) => (
  <TableCell {...cellProps} sortDirection={orderBy === sortKey ? order : false}>
    <TableSortLabel
      active={orderBy === sortKey}
      direction={orderBy === sortKey ? order : 'asc'}
      onClick={() => onRequestSort(sortKey)}
    >
      {label}
    </TableSortLabel>
  </TableCell>
);

export function toggleSort(
  currentKey: string,
  currentDir: SortDirection,
  nextKey: string
): { orderBy: string; order: SortDirection } {
  if (currentKey === nextKey) {
    return { orderBy: nextKey, order: currentDir === 'asc' ? 'desc' : 'asc' };
  }
  return { orderBy: nextKey, order: 'asc' };
}

/** Ordena un array en cliente por un getter de valor comparable. */
export function sortRows<T>(
  rows: T[],
  orderBy: string,
  order: SortDirection,
  getters: Record<string, (row: T) => string | number | boolean | null | undefined>
): T[] {
  const getter = getters[orderBy];
  if (!getter) return rows;

  const dir = order === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const va = getter(a);
    const vb = getter(b);

    if (va == null && vb == null) return 0;
    if (va == null) return 1;
    if (vb == null) return -1;

    if (typeof va === 'number' && typeof vb === 'number') {
      return (va - vb) * dir;
    }
    if (typeof va === 'boolean' && typeof vb === 'boolean') {
      return (Number(va) - Number(vb)) * dir;
    }

    return String(va).localeCompare(String(vb), 'es', { sensitivity: 'base' }) * dir;
  });
}

export default SortableTableCell;
